import { APP_BASE_HREF, DOCUMENT } from '@angular/common';
import { ApplicationRef, Inject, Injectable, Optional } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { routerNavigatedAction, routerNavigationAction } from '@ngrx/router-store';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { isEqual } from 'lodash-es';
import { Subject, combineLatest, merge, race } from 'rxjs';
import {
  combineLatestWith,
  delay,
  distinctUntilChanged,
  filter,
  first,
  map,
  switchMap,
  take,
  takeWhile,
  tap,
} from 'rxjs/operators';
import { ProductReviewsFacade } from 'src/app/extensions/rating/facades/product-reviews.facade';

import { ShoppingFacade } from 'ish-core/facades/shopping.facade';
import { AttributeHelper } from 'ish-core/models/attribute/attribute.helper';
import { CategoryView } from 'ish-core/models/category-view/category-view.model';
import { CategoryHelper } from 'ish-core/models/category/category.model';
import { REQUEST } from 'ish-core/models/express-tokens/express.tokens';
import { ProductListingID, ProductListingView } from 'ish-core/models/product-listing/product-listing.model';
import { ProductView } from 'ish-core/models/product-view/product-view.model';
import { ProductCompletenessLevel, ProductHelper } from 'ish-core/models/product/product.model';
import { SeoAttributes } from 'ish-core/models/seo-attributes/seo-attributes.model';
import { SeoSchemaHelper } from 'ish-core/models/seo-schema/seo-schema.helper';
import { generateCategoryUrl, ofCategoryUrl } from 'ish-core/routing/category/category.route';
import { ofContentPageUrl } from 'ish-core/routing/content-page/content-page.route';
import { generateProductUrl, ofProductUrl } from 'ish-core/routing/product/product.route';
import { JsonLdService } from 'ish-core/services/json-ld/json-ld.service';
import { getSelectedContentPage } from 'ish-core/store/content/pages';
import { getAvailableLocales, getCurrentLocale } from 'ish-core/store/core/configuration';
import { ofUrl, selectPath, selectRouteData, selectRouteParam } from 'ish-core/store/core/router';
import { personalizationStatusDetermined } from 'ish-core/store/customer/user/user.actions';
import {
  getBreadcrumbForCategoryPage,
  getSelectedCategory,
} from 'ish-core/store/shopping/categories/categories.selectors';
import { loadProductsForFilter } from 'ish-core/store/shopping/filter';
import {
  getBreadcrumbForProductPage,
  getProduct,
  getSelectedProduct,
  loadProductsForCategory,
} from 'ish-core/store/shopping/products';
import { searchProducts } from 'ish-core/store/shopping/search';
import { DomService } from 'ish-core/utils/dom/dom.service';
import { InjectSingle } from 'ish-core/utils/injection';
import { delayUntil, mapToPayload, mapToProperty, whenTruthy } from 'ish-core/utils/operators';

import { SeoService } from '../../services/seo/seo.service';

@Injectable()
export class SeoEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private domService: DomService,
    private metaService: Meta,
    private titleService: Title,
    private translate: TranslateService,
    private shoppingFacade: ShoppingFacade,
    private productReviewsFacade: ProductReviewsFacade,
    private jsonLdService: JsonLdService,
    private seoService: SeoService,
    @Inject(DOCUMENT) private doc: Document,
    @Optional() @Inject(REQUEST) private request: InjectSingle<typeof REQUEST>,
    @Inject(APP_BASE_HREF) private baseHref: string,
    private appRef: ApplicationRef
  ) {}

  private pageTitle$ = new Subject<string>();
  private pageDescription$ = new Subject<string>();

  private productPage$ = this.store.pipe(
    ofProductUrl(),
    select(getSelectedProduct),
    filter(p => ProductHelper.isSufficientlyLoaded(p, ProductCompletenessLevel.Detail)),
    filter(p => !ProductHelper.isFailedLoading(p))
  );

  private categoryPage$ = this.store.pipe(
    ofCategoryUrl(),
    select(getSelectedCategory),
    filter(CategoryHelper.isCategoryCompletelyLoaded)
  );

  private contentPage$ = this.store.pipe(ofContentPageUrl(), select(getSelectedContentPage));

  jsonLdProduct$ = createEffect(
    () =>
      this.productPage$.pipe(
        whenTruthy(),
        first(),
        delayUntil(this.actions$.pipe(ofType(personalizationStatusDetermined))),
        switchMap(product =>
          combineLatest([
            this.shoppingFacade.productPrices$(product.sku),
            this.productReviewsFacade.getProductReviews$(product.sku),
          ]).pipe(map(([prices, reviews]) => ({ product, prices, reviews })))
        ),
        tap(({ product, prices, reviews }) => {
          this.jsonLdService.setProductData(product, prices, reviews);
        })
      ),
    { dispatch: false }
  );
  jsonLdProductBreadcrumb$ = createEffect(
    () =>
      this.productPage$.pipe(
        whenTruthy(),
        first(),
        switchMap(() => this.store.pipe(select(getBreadcrumbForProductPage))),
        whenTruthy(),

        tap(breadcrumbData => this.jsonLdService.setBreadcrumbData(breadcrumbData))
      ),
    { dispatch: false }
  );
  jsonLdCategoryBreadcrumb$ = createEffect(
    () =>
      this.categoryPage$.pipe(
        whenTruthy(),
        first(),
        switchMap(() => this.store.pipe(select(getBreadcrumbForCategoryPage))),
        whenTruthy(),
        tap(breadcrumbData => this.jsonLdService.setBreadcrumbData(breadcrumbData))
      ),
    { dispatch: false }
  );

  jsonLdCategory$ = createEffect(
    () =>
      this.categoryPage$.pipe(
        whenTruthy(),
        first(),
        map(category => {
          this.jsonLdService.setFaqPageData(
            SeoSchemaHelper.generateFaqSchemaDataFromAttributes(
              category?.attributes ? this.jsonLdService.mapToCategoryFAQAttributes(category?.attributes) : []
            )
          );
        })
      ),
    { dispatch: false }
  );

  seoCanonicalLink$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(routerNavigatedAction),
        switchMap(() =>
          race([
            // PRODUCT PAGE
            this.productPage$
              .pipe(
                map(product => {
                  const isVariantProduct = ProductHelper.isVariationProduct(product);

                  if (isVariantProduct && product?.productMaster?.sku) {
                    let masterURL: string;
                    this.store
                      .pipe(select(getProduct(product.productMaster.sku)))
                      .pipe(
                        map(productMaster => {
                          if (!productMaster.name) {
                            productMaster.name = AttributeHelper.getAttributeValueByAttributeName(
                              product.attributes,
                              'productMasterDisplayName'
                            );
                          }
                          return (
                            this.baseURL + generateProductUrl(productMaster, product?.defaultCategory).substring(1)
                          );
                        })
                      )
                      .subscribe(url => (masterURL = url));
                    return masterURL;
                  }

                  if (!isVariantProduct) {
                    return this.baseURL + generateProductUrl(product, product?.defaultCategory).substring(1);
                  }

                  return;
                })
              )
              .pipe(whenTruthy()),
            // CATEGORY / FAMILY PAGE
            this.categoryPage$.pipe(
              map((category: CategoryView) => {
                const baseAndCategoryURL = generateCategoryUrl(category).substring(1);
                const pageRegex = /[;?](?!page=1[\D]*)page=[0-9]+/;
                if (!pageRegex.test(this.doc.URL)) {
                  return this.baseURL + baseAndCategoryURL;
                }
                const pageParameter = this.doc.URL.match(pageRegex);
                return this.baseURL + baseAndCategoryURL + pageParameter;
              })
            ),
            // DEFAULT
            this.appRef.isStable.pipe(
              whenTruthy(),
              map(() => this.doc.URL.replace(/[;?].*/g, ''))
            ),
          ])
        ),
        distinctUntilChanged(),
        tap(url => {
          this.seoService.setCanonicalUrl(url);
        })
      ),
    { dispatch: false }
  );

  seoMetaData$ = createEffect(
    () =>
      merge(
        this.actions$.pipe(
          ofType(routerNavigationAction),
          // DEFAULT or ROUTING
          switchMap(() => this.store.pipe(select(selectRouteData<SeoAttributes>('meta'))))
        ),
        this.actions$.pipe(
          ofType(routerNavigatedAction),
          switchMap(() =>
            race([
              // PRODUCT PAGE
              this.productPage$.pipe(
                map<ProductView, SeoAttributes>(p => ({
                  'og:image': ProductHelper.getPrimaryImage(p, 'L')?.effectiveUrl,
                  ...p.seoAttributes,
                })),
                whenTruthy()
              ),
              // CATEGORY / FAMILY PAGE
              this.categoryPage$.pipe(mapToProperty('seoAttributes'), whenTruthy()),
              // SEARCH RESULT PAGE
              this.store.pipe(
                ofUrl(/^\/search.*/),
                select(selectRouteParam('searchTerm')),
                whenTruthy(),
                switchMap(searchTerm => this.translate.get('seo.title.search', { 0: searchTerm })),
                map<string, Partial<SeoAttributes>>(title => ({ title }))
              ),
              // CONTENT PAGE
              this.contentPage$.pipe(mapToProperty('seoAttributes'), whenTruthy()),
            ])
          )
        )
      ).pipe(
        map(attributes => ({
          title: 'seo.defaults.title',
          description: 'seo.defaults.description',
          robots: 'index, follow',
          'og:type': 'website',
          'og:image': '/assets/img/og-image-default.jpg',
          ...attributes,
        })),
        distinctUntilChanged(isEqual),
        tap(seoAttributes => {
          this.setSeoAttributes(seoAttributes);
        })
      ),
    { dispatch: false }
  );

  seoLanguages$ = createEffect(
    () =>
      combineLatest([
        this.store.pipe(select(getCurrentLocale)),
        this.store.pipe(select(getAvailableLocales), whenTruthy()),
      ]).pipe(
        takeWhile(() => SSR),
        tap(([current, locales]) => {
          this.seoService.addOrModifyTag({ property: 'og:locale', content: current });

          this.metaService
            .getTags('property="og:locale:alternate"')
            .forEach(el => this.metaService.removeTagElement(el));
          locales
            .filter(lang => lang !== current)
            .forEach(lang => this.metaService.addTag({ property: 'og:locale:alternate', content: lang }, true));
        })
      ),
    { dispatch: false }
  );

  seoTitle$ = createEffect(
    () =>
      this.pageTitle$.pipe(
        delay(0), // ensures asynchronous stream processing to prevent "missing translation" issues
        switchMap(title =>
          combineLatest([
            this.translate.get(title),
            this.store.pipe(select(selectPath)),
            this.translate.get('seo.applicationName'),
          ])
        ),
        map(([title, path, application]) => [this.enhancePageTitle(title, path), application]),
        map(([title, application]) => `${title} | ${application}`),
        tap(title => {
          this.titleService.setTitle(title);
          this.seoService.addOrModifyTag({ property: 'og:title', content: title });
        })
      ),
    { dispatch: false }
  );

  seoDescription$ = createEffect(
    () =>
      this.pageDescription$.pipe(
        delay(0), // ensures asynchronous stream processing to prevent "missing translation" issues
        switchMap(description => this.translate.get(description)),
        tap(description => {
          this.seoService.addOrModifyTag({ name: 'description', content: description });
          this.seoService.addOrModifyTag({ property: 'og:description', content: description });
        })
      ),
    { dispatch: false }
  );

  setSeoPaginationForCategoryPage =
    SSR &&
    createEffect(
      () =>
        this.actions$.pipe(
          ofType(loadProductsForCategory),
          mapToPayload(),
          filter(x => !!x?.categoryId),
          combineLatestWith(this.shoppingFacade.selectedCategory$),
          filter(([, category]) => !category.hasChildren),
          switchMap(([payload]) =>
            this.shoppingFacade.productListingView$({
              type: 'category',
              value: payload.categoryId,
              page: payload?.page || 1,
            })
          ),
          distinctUntilChanged(isEqual),
          filter(x => !!x?.itemCount),
          take(1),
          map(view => this.processSeoSearchPaginationTag(view))
        ),
      {
        dispatch: false,
      }
    );

  setSeoPaginationForSearchRelatedPage =
    SSR &&
    createEffect(
      () =>
        this.actions$.pipe(
          ofType(searchProducts, loadProductsForFilter),
          take(1),
          mapToPayload(),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          map((payload: any): ProductListingID => {
            const searchPayload = payload;
            return {
              type: 'search',
              value: searchPayload?.id?.value || searchPayload?.searchTerm,
              page: searchPayload?.id?.page || searchPayload?.page || 1,
              filters: searchPayload?.id?.filters,
            };
          }),
          switchMap(productListingId => this.shoppingFacade.productListingView$(productListingId)),
          distinctUntilChanged(isEqual),
          filter(x => !!x?.itemCount),
          take(1),
          map(view => this.processSeoSearchPaginationTag(view))
        ),
      {
        dispatch: false,
      }
    );

  private processSeoSearchPaginationTag(view: ProductListingView) {
    if (view?.previousPage()) {
      this.createSeoSearchPaginationTag('prev', view.previousPage());
    }
    if (view?.nextPage()) {
      this.createSeoSearchPaginationTag('next', view.nextPage());
    }
  }

  private createSeoSearchPaginationTag(key: string, value: number) {
    const queryParam = value && value !== 1 ? `?page=${value}` : '';

    const link = this.domService.createElement<HTMLLinkElement>('link', this.doc.head);
    this.domService.setProperty(link, 'rel', key);

    this.domService.setAttribute(link, 'href', `${this.doc.URL.replace(/[;?].*/g, '')}${queryParam}`);
  }

  /* PDU - 43 End  */

  private get baseURL() {
    let url: string;
    if (this.request) {
      url = `${this.request.protocol}://${this.request.get('host')}${this.baseHref}`;
    } else {
      url = this.doc.baseURI;
    }
    return this.getBaseURL(url);
  }

  private setSeoAttributes(seoAttributes: SeoAttributes) {
    Object.entries(seoAttributes)
      .filter(([, v]) => !!v)
      .forEach(([key, value]) => {
        switch (key) {
          case 'title':
            this.pageTitle$.next(value);
            return;
          case 'description':
            this.pageDescription$.next(value);
            return;
          case 'robots':
            this.seoService.addOrModifyTag({ name: key, content: value });
            return;
        }
        this.seoService.addOrModifyTag({ property: key.startsWith('og:') ? key : `og:${key}`, content: value });
      });
    this.seoService.addOrModifyTag({ property: 'pwa-version', content: PWA_VERSION });
  }

  private getBaseURL(url: string) {
    return url.endsWith('/') ? url : `${url}/`;
  }

  // Adapt the page title for specific pages
  private enhancePageTitle(title: string, path: string): string {
    let pageSuffix = '';
    if (path?.startsWith('account/')) {
      pageSuffix = `${this.translate.instant('account.my_account.heading')}`;
    }
    if (path?.startsWith('checkout/')) {
      pageSuffix = `${this.translate.instant('seo.title.checkout')}`;
    }
    return pageSuffix && pageSuffix !== title ? `${title} - ${pageSuffix}` : title;
  }
}
